/**
 * View Component: PostDetail
 * Right drawer showing full post content with A/B version toggle.
 *
 * Props:
 *   post: post object or null
 *   open: boolean
 *   onClose: () => void
 *   onToggleFav: (id) => void
 */

// T-Cron-2026-10-24 02:00 早间 (round-1-of-day) — 改进清单 #8「批量操作」。
//
// 收口三处重复实现到共享模块,避免行为漂移:
//   · 本文件自带的 buzz()          → harness/haptics.js(唯一一份「尊重
//                                    prefers-reduced-motion」的判断)
//   · 本文件自带的 copyTextToClipboard() → harness/clipboard.js(与批量复制同源)
//   · 本文件的 getVersionData / tagsLine → harness/postText.js(与批量复制同源)
//
// 本文件是 .jsx 且含 JSX ⇒ React 由 vite.config.js 的 esbuild.jsxInject
// 全局注入,这里**不要**显式 import React。
import { copyToClipboard } from '../harness/clipboard.js';
import { buzz as buzzHaptic } from '../harness/haptics.js';
import { getVersionData, buildAllVersionsText, countCopyChars, resolveVersionKey } from '../harness/postText.js';
// T-Cron-2026-10-28 03:30 (T118) — 改进清单 #1「复制体验」:最近复制历史。
// 老仓库(根目录 components/PostDetail.jsx,老 Babel 分叉)T43 就有「复制历史」,
// Vite 重写时丢了 ⇒ 详见 harness/copyHistory.js 顶部的缺口说明。
import { useCopyHistory } from '../harness/copyHistory.js';
// T-Cron-2026-10-31 08:00:下载结果 toast 收口到 harness/DismissibleToast.jsx
// (点击 / Esc 关闭 + 键盘可达),4 个 toast 入口行为一致。
import { DismissibleToast } from '../harness/DismissibleToast.jsx';

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <line x1="18" y1="6" x2="6" y2="18"></line>
      <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
      <polyline points="7 10 12 15 17 10"></polyline>
      <line x1="12" y1="15" x2="12" y2="3"></line>
    </svg>
  );
}

function HeartSmallIcon({ filled }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
    </svg>
  );
}

function PostDetail({ post, open, onClose, onToggleFav, viewCount = 0, preferredVersion = '' }) {
  const [copied, setCopied] = React.useState(false);
  const [downloading, setDownloading] = React.useState(null); // 正在下载的版本号 A/B/C
  // 🔴 T135(2026-11-02 20:00):下载防重入的**真守卫**。
  // `downloading` state 只用来驱动 UI(按钮 disabled / 进度条文案);
  // 真正的「已经在下载了」判定必须用 ref —— 因为键盘 `d` 的事件监听器
  // 是在 useEffect 里注册的,闭包会冻结 ⇒ 读到的是**注册那一刻**的
  // state 值,不是按下那一刻的值 ⇒ 用 state 做守卫会漏。
  // (与上面的 copyHandlerRef 同一类问题的同一套修法。)
  const downloadingRef = React.useRef(false);
  const [toastMsg, setToastMsg] = React.useState('');
  const [sheet, setSheet] = React.useState(null); // 多方案保存面板 { ver, url }
  // T-Cron-2026-10-23 08:00 — 改进清单 #5「进度条:下载封面进度」。
  // { loaded, total } —— total=0 表示总大小未知(不确定态进度条)。
  const [dlProgress, setDlProgress] = React.useState(null);

  // 华为 / 鸿蒙浏览器：a[download]+blob 链路会被拦截，面板内隐藏直接下载
  // （渲染时直读 UA，保证环境检测始终新鲜）
  const huaweiEnv = CoverDownload.isHuawei();

  // 当前环境是否支持系统级文件分享（iOS 分享面板可直接「存储图像」）
  const canShareCover = React.useMemo(
    () => CoverDownload.canShareFiles(new Blob([new Uint8Array([1])], { type: 'image/jpeg' })),
    []
  );

  // 🔴 T-Cron-2026-11-01 20:00 晚间 — 修「打开任意卡片即白屏」(P0)。
  //
  // 【现象】点击任意一张卡片 → 整个看板跳到 ErrorBoundary
  //         (「看板出了点问题」)⇒ 500+ 条选题的主路径全断。
  //
  // 【根因:用 effect 同步 state,导致首帧读到陈旧值】
  //   旧写法(14:00 轮引入):
  //       const [activeVersion, setActiveVersion] = useState('A');
  //       useEffect(() => setActiveVersion(resolveVersionKey(post, preferredVersion)),
  //                 [post && post.id, preferredVersion]);
  //
  //   抽屉**关闭**时 post 是 null ⇒ 第一次 effect 跑出 resolveVersionKey(null) = ''
  //   ⇒ setActiveVersion('') 把 state 从 'A' 洗成了 **''**。
  //
  //   然后用户点卡片 ⇒ post 变成真对象 ⇒ 这一帧 activeVersion 仍然是 **''**
  //   (effect 要等这一帧**渲染完之后**才跑)⇒
  //       getVersionData(post, '') 找不到 post.versions['']
  //       ⇒ 回退到扁平 post ⇒ post 没有 .title 字段
  //       ⇒ versionData.title.length → **TypeError**
  //       ⇒ ErrorBoundary 接管 ⇒ 白屏。
  //
  //   ⇒ 「post 从 null 变成对象」和「state 修正」之间有**一帧的窗口**,
  //     而那一帧恰好就是抽屉第一次渲染的那一帧。
  //
  // 【修法:React 官方推荐的「render 期调整 state」模式】
  //   把「用户手动切到的版本」和「外部决定的版本」分开:
  //     · userVersion —— 用户在抽屉里点的 A/B/C(可能为 null)
  //     · resolved   —— 本帧真正该用的版本 = resolveVersionKey(post, preferred)
  //   render 期直接算出 resolved(纯函数,无副作用)⇒ **不存在陈旧帧**。
  //   只有当「换了选题」或「全局版本筛选变了」时,才把 userVersion 重置为 null
  //   (这正是 React 文档里 "adjusting state when props change" 的写法:
  //    在 render 里比较 prev prop,变了就直接 setState,React 会立刻重渲染)。
  //
  // ⚠️ 为什么不能简单地把初值写死成 'A':
  //   万一某篇 post 真的没有 A 版(只有 B/C),'A' 同样是无效键 ⇒ 同样的崩溃。
  //   ⇒ 必须在 render 期用 resolveVersionKey 求出**必然有效**的键。
  //
  // ⚠️ 依赖数组用 post.id(不是 post):
  //   收藏会让 store 重建 post 对象 ⇒ 若依赖 post,收藏一下就会重置
  //   用户手动切到的版本 ⇒ 回归。
  const [userVersion, setUserVersion] = React.useState(null);
  const [lastPostId, setLastPostId] = React.useState(null);
  const [lastPreferred, setLastPreferred] = React.useState(null);

  // render 期调整:换了选题 / 改了全局版本筛选 ⇒ 丢弃用户手动选择。
  if ((post && post.id) !== lastPostId || preferredVersion !== lastPreferred) {
    setLastPostId(post ? post.id : null);
    setLastPreferred(preferredVersion);
    setUserVersion(null);
  }

  // 本帧真正使用的版本:用户手选的优先,否则用共享解析口径。
  // 🔴 resolveVersionKey 是纯函数 ⇒ render 期调用安全,且**没有陈旧帧**。
  const resolvedVersion = userVersion || resolveVersionKey(post, preferredVersion);

  // 换选题 / 换筛选时,用户手选的版本可能在新选题上不存在 ⇒ 兜底校验。
  const activeVersion =
    (post && post.versions && post.versions[resolvedVersion])
      ? resolvedVersion
      : resolveVersionKey(post, preferredVersion);

  // T-Cron-2026-10-28 03:30 (T118) — 改进清单 #1「复制体验」:最近复制历史。
  //
  // 为什么状态放在 PostDetail 而不是 App:
  //   历史是**详情页的上下文**(属于哪本书、哪个版本、复制了哪一块),
  //   只有详情抽屉开着的时候用户才会去看它 ⇒ 不需要跨页面共享。
  //   App 层已经有一份搜索历史(useSearchHistory),那是**跨页面**的语义,
  //   两者生命周期不同,不要合并成一个"历史"概念。
  const { history: copyHistory, push: pushCopy, clear: clearCopies } = useCopyHistory();

  // 记录一次复制。charCount 在这里算一次 —— 历史列表里直接显示字数,
  // 用户判断「会不会超小红书 1000 字」时不用切回详情页。
  const recordCopy = (text, kind, version) => {
    if (!text) return;
    pushCopy({
      ts: Date.now(),
      kind,
      book: (post && post.book) || '',
      version: version || activeVersion,
      text,
      charCount: countCopyChars(text),
    });
  };

  const toastTimer = React.useRef(null);
  const showToast = (msg) => {
    setToastMsg(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastMsg(''), 2600);
  };

  // 短震动反馈(支持设备才会触发)
  // T-Cron-2026-10-24:收口到 harness/haptics.js。本文件原来的 buzz() 没有
  // 判断 prefers-reduced-motion ⇒ 开了「减弱动态效果」的用户在这里仍会收到
  // 震动,而 PostCard 那边会跳过 —— 同一台手机上表现不一致。
  const buzz = (pattern = 30) => buzzHaptic(pattern);

  // 移动端 Sheet 滑动手势:从顶部 handle 向下拖 > 阈值则关闭
  const dragStartY = React.useRef(0);
  const dragDeltaY = React.useRef(0);
  const [dragOffset, setDragOffset] = React.useState(0);
  const [showCopyPulse, setShowCopyPulse] = React.useState(false);

  const isTouch = CoverDownload.isTouch();
  const handleDragStart = (e) => {
    if (!isTouch) return;
    // 只在用户从顶部 30px 内开始拖动才触发
    const scrollTop = e.currentTarget.scrollTop || 0;
    if (scrollTop > 4) return;
    dragStartY.current = e.touches[0].clientY;
  };
  const handleDragMove = (e) => {
    if (!isTouch || dragStartY.current === 0) return;
    const dy = e.touches[0].clientY - dragStartY.current;
    if (dy < 0) { dragStartY.current = e.touches[0].clientY; return; }
    dragDeltaY.current = dy;
    setDragOffset(dy);
  };
  const handleDragEnd = () => {
    if (!isTouch) return;
    if (dragDeltaY.current > 120) {
      buzz(20);
      onClose();
    }
    dragStartY.current = 0;
    dragDeltaY.current = 0;
    setDragOffset(0);
  };

  // 🔴 T-Cron-2026-11-01 20:00 晚间 — 上面已经把「本帧该用哪个版本」
  //   改成 render 期直接推导(activeVersion),**不再需要这个 effect**。
  //
  //   保留它的唯一理由是「换一篇 post 时复位版本」——
  //   但那件事现在由 render 期调整 state 那一段统一做了
  //   (换了 post.id ⇒ setUserVersion(null) ⇒ 回落 resolveVersionKey)。
  //
  //   ⚠️ 为什么必须删掉这个 effect:
  //     它会把 activeVersion 洗成 resolveVersionKey(null) = ''(抽屉关闭时 post 为 null),
  //     而 effect 晚于 render 一帧 ⇒ 点开卡片的那一帧 activeVersion 仍是 ''
  //     ⇒ versionData 回退扁平 post ⇒ versionData.title undefined
  //     ⇒ versionData.title.length 抛 TypeError ⇒ 整个看板白屏。
  //     这就是 T129 实测到的 P0 崩溃(点任意卡片即白屏,100% 复现)。

  // Get version data safely
  // T-Cron-2026-10-24:本地那份实现与 harness/postText.js 的 getVersionData
  // 逐字相同,已删除并改为 import 共享版本 —— 批量复制与详情必须用同一套
  // 版本解析规则,否则两处会挑到不同的版本。
  const hasAB = post && post.versions && Object.keys(post.versions).length > 1;
  const versionKeys = post && post.versions ? Object.keys(post.versions).sort() : [];
  const versionData = getVersionData(post, activeVersion);
  // Cover switches with the active version; falls back to the post-level cover
  const detailCoverUrl = (versionData && versionData.coverUrl) || (post && post.coverUrl) || '';

  // 🔴 T114 (2026-10-27):详情抽屉里的封面加载失败降级。
  //
  // 详情抽屉是封面**唯一**能被用户放大看、被长按保存、被下载的地方 ——
  // 也就是说封面在这里坏掉,损失最大(用户以为封面本来就没有)。
  //
  // CF Pages 对不存在的静态文件返回 200 + index.html(SPA 回退)
  // ⇒ <img> 触发 onerror ⇒ 浏览器碎图标 + alt 文字。
  // 这里降级成明确的提示块,并把「保存封面」按钮也一并隐藏 ——
  // 保存一张取不到的封面只会得到一个打不开的 HTML 文件。
  const [detailCoverBroken, setDetailCoverBroken] = React.useState(false);

  React.useEffect(() => {
    setDetailCoverBroken(false);
  }, [detailCoverUrl]);

  // Tags are appended to the end of the body text (hashtag style);
  // the merged word count includes tag characters.
  const tagsList = (versionData && versionData.tags) || [];
  const tagsLine = tagsList.map(tag => `#${tag}`).join(' ');
  const tagCharCount = tagsList.reduce((sum, tag) => sum + tag.length, 0);
  const totalWordCount = ((versionData && versionData.wordCount) || 0) + tagCharCount;

  /**
   * 保存指定版本的封面（A/B/C 均可独立保存）。
   * 触屏设备（含华为/鸿蒙、微信/飞书 webview）：直接弹「多方案保存面板」——
   * 长按保存为主，系统分享/直接下载/复制链接/浏览器打开兜底，
   * 彻底绕开华为浏览器上被拦截的 a[download]+blob 链路。
   * 桌面：保留一键直接下载。
   */
  const fileNameFor = (ver) =>
    `${(post && post.book ? post.book : '封面').replace(/[\\/:*?"<>|]/g, '')}-${ver}版-封面.jpg`;

  const handleSaveCover = async (ver) => {
    const vData = getVersionData(post, ver);
    const url = (vData && vData.coverUrl) || (post && post.coverUrl);
    if (!url) return;
    // 🔴 T135(2026-11-02 20:00):防重入。
    //
    // 原来这里判的是 `downloading`(**state**)。但键盘 `d` 的 listener
    // 是在 useEffect 里注册的,依赖数组只有 [open, post, activeVersion]
    // —— `downloading` 不在里面 ⇒ 连续按两次 `d` 时,第二次拿到的
    // `downloading` 仍是**旧值**(null)⇒ 守卫失效 ⇒ 两个下载并发跑,
    // 进度条在两条流之间跳,用户可能拿到两份封面。
    //
    // 修法:用 **ref** 记「正在下载」—— ref 不受闭包过期影响
    // (读到的一定是最新值),这正是它比 state 更适合做守卫的地方。
    // state 只用来驱动 UI(按钮 disabled / 进度条),ref 才是真守卫。
    if (downloadingRef.current) return;
    downloadingRef.current = true;

    // 触屏：多方案保存面板（不先尝试下载——华为/鸿蒙上必失败或被静默拦截）
    if (CoverDownload.isTouch()) {
      downloadingRef.current = false;
      setSheet({ ver, url });
      return;
    }

    // 桌面：一键直接下载（jpg 直取文件 blob，svg 经 canvas）
    setDownloading(ver);
    setDlProgress({ loaded: 0, total: 0 });
    try {
      const mode = await CoverDownload.download(url, fileNameFor(ver), (p) => setDlProgress(p));
      if (mode === 'shared') {
        showToast('已调起系统分享，选择「存储图像」即可保存');
      } else if (mode === 'webview-anchor') {
        setSheet({ ver, url });
      } else if (mode !== 'cancel') {
        showToast('已开始下载封面，若未成功可长按封面图保存');
      }
    } catch (e) {
      setSheet({ ver, url });
    } finally {
      downloadingRef.current = false;
      setDownloading(null);
      setDlProgress(null);
    }
  };

  const handleSheetShare = async () => {
    if (!sheet) return;
    // 🔴 T135 补漏:面板内「系统分享保存」此前**没有**防重入守卫
    //   (handleSaveCover / handleShareCover 都有)。
    //   ⇒ 连点两次会并发调两次 navigator.share ⇒ iOS 抛 InvalidStateError,
    //   或弹两个分享面板。
    if (downloadingRef.current) return;
    downloadingRef.current = true;
    setDownloading(sheet.ver);
    setDlProgress({ loaded: 0, total: 0 });
    try {
      const blob = await CoverDownload.toJpegBlob(sheet.url, (p) => setDlProgress(p));
      // 🔴 T136:shareBlob 现在返回语义化档位。
      //   'cancel' = 用户自己关掉了分享面板 ⇒ **静默**,不弹任何 toast,
      //   也不要说「不支持」(用户没有做错任何事)。
      const r = await CoverDownload.shareBlob(blob, fileNameFor(sheet.ver));
      if (r === 'unsupported') showToast('当前环境不支持系统分享，请长按图片保存');
      else if (r === 'failed') showToast('分享未完成，请长按图片保存');
    } catch (e) {
      showToast('当前环境不支持系统分享，请长按图片保存');
    } finally {
      downloadingRef.current = false;
      setDownloading(null);
      setDlProgress(null);
    }
  };

  // 面板内「直接下载」：仅非华为环境展示（华为/鸿蒙浏览器该链路会被拦截）
  //
  // 🔴🔴 T149 (2026-11-05 08:00):防重入守卫 —— 本函数是**唯一**还在用
  // `downloading`(**state**)当守卫的处理器。
  //
  // 【原实现的错在哪】
  //   if (!sheet || downloading) return;   // ← 读的是 React state
  //   setDownloading(sheet.ver);           // ← state 更新是**异步**的
  //
  //   同族的 handleSaveCover(300 行)/handleSheetShare(337 行)/
  //   handleShareCover(401 行)**全都**用 `downloadingRef.current` 当守卫
  //   —— 上面 289~299 行的注释已经把这条讲透了:
  //   「state 只用来驱动 UI(按钮 disabled / 进度条),ref 才是真守卫」。
  //   ⇒ 本函数是**漏网的那一个**。
  //
  // 【为什么在本项目是可达的 —— 不是理论问题】
  //   面板内「直接下载」按钮的 disabled 绑定是 `downloading === sheet.ver`
  //   (1461 行)⇒ 它要等 React **提交**这次 state 更新后才禁用。
  //   而 React 的 state 更新是异步批处理的 ⇒ **同一个 tick 内的第二次点击**
  //   读到的还是旧值 null ⇒ 守卫失效 ⇒ 两次下载并发跑。
  //   实测(本地 Playwright,把封面 fetch 放慢 2.5s 放大窗口):
  //     连点两次「直接下载」⇒ **封面 fetch 被触发 2 次**
  //     (同一张封面,各 ~200~600KB)⇒ 流量翻倍 + 两条进度条互相覆盖。
  //
  // 【为什么静默放行更糟】
  //   `CoverDownload.download()` 内部会再调一次 `navigator.share`
  //   (华为/鸿蒙之外走系统分享,桌面走 a[download])⇒ 两个并发的
  //   a[download] 会触发**两次浏览器下载确认**,或者第二个分享面板
  //   撞上 InvalidStateError ⇒ 用户看到的是「莫名其妙弹了两遍」。
  //
  // 【修法】与同族一致:ref 当守卫 + ref 置位 + finally 复位。
  //   ⚠️ 必须**同时**置位 ref —— 只把守卫从 state 换成 ref 而不置位,
  //      等于把「双击并发」变成「永不并发但也挡不住别人」,
  //      是**更糟**的退化(下载进行中时,别的处理器就能插进来)。
  const handleSheetDownload = async () => {
    if (!sheet) return;
    // 🔴 T149:ref 才是真守卫(state 只驱动 UI)。
    if (downloadingRef.current) return;
    downloadingRef.current = true;
    setDownloading(sheet.ver);
    setDlProgress({ loaded: 0, total: 0 });
    try {
      const mode = await CoverDownload.download(sheet.url, fileNameFor(sheet.ver), (p) => setDlProgress(p));
      if (mode === 'shared') showToast('已调起系统分享，选择「存储图像」即可保存');
      else if (mode !== 'cancel') showToast('已尝试下载，若未成功请长按图片保存');
    } catch (e) {
      showToast('直接下载不可用，请长按图片保存');
    } finally {
      // 🔴 T149:与其它处理器同口径 —— finally 里复位 ref,
      //    否则一次失败之后 ref 永远为 true ⇒ 后续下载全部被挡死。
      downloadingRef.current = false;
      setDownloading(null);
      setDlProgress(null);
    }
  };

  const handleSheetCopyLink = async () => {
    if (!sheet) return;
    const ok = await CoverDownload.copyLink(sheet.url);
    showToast(ok
      ? '图片链接已复制，可在浏览器打开链接后长按保存'
      : '复制失败，请长按图片保存');
  };

  // T-Cron-2026-10-28 03:30 (T118) — 改进清单 #2「快捷键」补齐:`s` 快捷键。
  //
  // 为什么不能直接复用 handleSheetShare:
  //   它依赖 `sheet` 状态(只有在长按/点「保存封面」打开面板后才有 sheet),
  //   键盘快捷键是**直接意图**:用户按 `s` 就是要分享,不该先弹一个
  //   三选一面板再让他点一次「分享」—— 那是把快捷键退化成了普通按钮。
  //
  // 流程与 handleSheetShare 相同(toJpegBlob → shareBlob),区别只是
  // url 直接取当前版本的封面,不走 sheet 中转。
  const handleShareCover = async () => {
    const vData = getVersionData(post, activeVersion);
    const url = (vData && vData.coverUrl) || (post && post.coverUrl);
    if (!url) {
      showToast('这一版没有封面可分享');
      return;
    }
    // 🔴 T135(2026-11-02 20:00):防重入(同 handleSaveCover)。
    // 连按两次 `s` ⇒ 两次 shareBlob 并发 ⇒ 系统分享面板弹两次 / 两次 fetch。
    if (downloadingRef.current) return;
    downloadingRef.current = true;
    // 🔴 T136(2026-11-03 02:00):快捷键 `s` 是一条**绕过 UI 守卫**的入口。
    //   面板里的「系统分享保存」按钮受 canShareCover 门控,
    //   但按 `s` 会直接进 shareBlob ⇒ 在 navigator.share 不存在的环境里
    //   白跑一次完整 fetch(200-600KB)才报「不支持」,浪费流量且误导。
    //   ⇒ 在这里提前挡掉,直接给面板兜底。
    if (typeof navigator.share !== 'function') {
      downloadingRef.current = false;
      setSheet({ ver: activeVersion, url });
      showToast('当前环境不支持系统分享，可改用下载或复制链接');
      return;
    }
    setDownloading(activeVersion);
    setDlProgress({ loaded: 0, total: 0 });
    try {
      const blob = await CoverDownload.toJpegBlob(url, (p) => setDlProgress(p));
      // 🔴 T136:shareBlob 返回语义化档位。
      //   'cancel' = 用户自己关掉了分享面板 ⇒ **静默**:
      //   不弹 toast,也不自动打开下载面板(那会凭空冒出一个弹层挡住内容)。
      const r = await CoverDownload.shareBlob(blob, fileNameFor(activeVersion));
      if (r === 'unsupported') {
        // 系统分享不可用(桌面 Chrome / Firefox 没有 navigator.share)
        // ⇒ 退回「打开面板」,让用户至少能下载或复制链接。
        setSheet({ ver: activeVersion, url });
        showToast('当前环境不支持系统分享，可改用下载或复制链接');
      } else if (r === 'failed') {
        setSheet({ ver: activeVersion, url });
        showToast('分享失败，请改用下载或复制链接');
      }
    } catch (e) {
      setSheet({ ver: activeVersion, url });
      showToast('分享失败，请改用下载或复制链接');
    } finally {
      downloadingRef.current = false;
      setDownloading(null);
      setDlProgress(null);
    }
  };

  const sheetOpenUrl = sheet ? new URL(sheet.url, window.location.href).href : '';

  /* T-Cron-2026-10-22 14:00 (round-2-of-day) — 改进清单 #1「复制体验」:
     修一个**静默失败** bug。

     原来的 handleCopy 是:
         navigator.clipboard.writeText(text).then(() => { ...成功反馈... });
     —— **没有 .catch()**。

     `navigator.clipboard.writeText()` 在以下环境会 reject 而不是 throw:
       · 非 HTTPS 上下文(内嵌 http iframe、部分 webview)
       · 权限被拒(Permissions-Policy: clipboard-write 未授权)
       · 华为/鸿蒙 webview、微信内置浏览器、飞书内置浏览器
     reject 之后 `.then()` 的成功分支根本不执行 ⇒ 按钮**永远没有反应**:
     没有「已复制 ✓」、没有 toast、没有震动。用户只能靠"点了没反应"猜测,
     多数人会直接认为按钮坏了并重复点击 —— 而实际上文案可能已经进了剪贴板,
     也可能根本没进,用户无从判断。

     三段式修复:
       1. 主路径 navigator.clipboard.writeText (现代浏览器 / PWA)
       2. 兜底 textarea + document.execCommand('copy') —— 老 webview 唯一可行的路
          (与 CoverDownload.copyLink 里已有的同一套兜底保持一致)
       3. 两条都失败 ⇒ 必须**告诉用户失败**,并给出可手动操作的下一步,
          不能静默返回。用户还能长按选中文案自己复制。

     成功反馈统一走 setCopied + showCopyPulse + buzz,两段成功路径共用,
     保证「复制成功」的视觉/触觉反馈只有一套实现,不会漂移。
     失败时额外 buzz 一个短促的单次震动,区别于成功的「两短一短」。 */
  const copyTextToClipboard = async (text) => {
    // T-Cron-2026-10-24:实现搬到 harness/clipboard.js,这里只保留调用点。
    // 行为与原来的三段式逐字等价(主路径 → 老 webview 兜底 → 失败返回 false),
    // 唯一区别是失败时**由调用方**负责给用户明确反馈(见下方 handleCopy /
    // copyFieldAndFeedback),语义没变。
    return copyToClipboard(text);
  };

  const handleCopy = async () => {
    if (!post || !versionData) return;
    const text = `${versionData.title}\n\n${versionData.body}${tagsLine ? `\n\n${tagsLine}` : ''}`;
    const ok = await copyTextToClipboard(text);
    if (ok) {
      setCopied(true);
      setShowCopyPulse(true);
      buzz([10, 30, 10]);   // 两短一短的「已复制」节奏
      // T-Cron-2026-10-28 (T118):记入「最近复制历史」。
      // charCount 用 harness/postText.js 的 countCopyChars(CJK 逐字 + 英文按词),
      // 与老仓库 T-Cron-2026-10-07 的口径一致 ⇒ 历史里显示的字数可信。
      recordCopy(text, 'full', activeVersion);
      setTimeout(() => setCopied(false), 1500);
      setTimeout(() => setShowCopyPulse(false), 420);
    } else {
      // 3) 彻底失败:必须给用户明确反馈,不能静默
      buzz(60);
      showToast('复制失败，请长按选中文案手动复制');
    }
  };

  // T-Cron-2026-10-26 03:30 早间 — 改进清单 #1「复制体验」补齐:
  // **一键复制 A/B/C 三版**(老仓库 PostDetail 有 handleCopyAll,Vite 重写时丢了)。
  //
  // 🔴 为什么这是「可达性缺口」而不是「新功能」:
  //   用户拿到一个选题,A/B/C 三版是三种**完全不同的角度**(热点主导 /
  //   书点睛 / 书籍主导)。真实工作姿势是「先横向比较三版,再决定发哪版」。
  //   而 Vite 版只有「复制当前版」⇒ 要横向比较就得:
  //     切 A → 复制 → 关抽屉 → 重新打开 → 切 B → 复制 → …
  //   三版 = 开三次抽屉 + 切三次 + 复制三次,而每次复制后还要手动去
  //   小红书编辑器里对比草稿 —— 这条路径在真实使用里几乎没人走完。
  //
  //   ⇒ 一键把三版拼成一段文本(带【A 标题】/【A 正文】小标题),
  //   粘到小红书编辑器里横向看,一次搞定。
  //
  // 文案口径统一在 harness/postText.js 的 buildAllVersionsText ——
  // 与「复制正文」「批量复制」同源,不会漂移。
  //
  // 快捷键:Shift+C(老仓库同约定)。单键 c 仍然只复制当前版。
  const handleCopyAllVersions = async () => {
    if (!post) return;
    const text = buildAllVersionsText(post);
    if (!text) {
      buzz(60);
      showToast('该选题暂无可复制内容');
      return;
    }
    const ok = await copyTextToClipboard(text);
    if (ok) {
      buzz([10, 30, 10]);
      // T-Cron-2026-10-28 (T118):三版合一的文案也进历史 —— 用户常把它
      // 粘到编辑器里横向比较,失败重来时想直接再拿一次。
      recordCopy(text, 'all', activeVersion);
      showToast('已复制 A/B/C 三版');
      setLiveMsg('已复制 A/B/C 三版');
    } else {
      buzz(60);
      showToast('复制失败，请长按选中文案手动复制');
      setLiveMsg('复制失败，请长按选中文案手动复制');
    }
  };

  // T-Cron-2026-10-23 20:00 晚间 — 改进清单 #1「复制体验」补齐:
  //
  // 详情抽屉里**每个可复制的块**都应该能单独一键复制,而不是只有顶部
  // 一个「复制文案」按钮把全文拼一起。用户真正的使用姿势是:
  //   「标题我用我自己的,正文我只抄 A 版那一段」——
  // 现在要么全拿要么全不拿,没有中间粒度。
  //
  // 这三个按钮直接复用已有的 copyTextToClipboard(主路径 + 老 webview 兜底 +
  // 失败提示三段式),所以**不新增任何复制实现**,只新增调用点 ——
  // 复制语义天然保持一致。
  //
  // 每个按钮独立记 state(copyField),成功时:
  //   · 按钮本身变成绿色 .is-copied 1.6s(指尖确认,不用眼睛去找 toast)
  //   · 震动一次
  //   · toast 播报 + role="status"(读屏用户也能听到)
  const [copyField, setCopyField] = React.useState(null);
  const [failedField, setFailedField] = React.useState(null);
  const copyFieldTimer = React.useRef(null);
  const [liveMsg, setLiveMsg] = React.useState('');

  const copyFieldAndFeedback = async (field, text, label) => {
    const ok = await copyTextToClipboard(text);
    if (!ok) {
      // 失败也要有指尖反馈:按钮转红 + 失败文案,而不是什么都没发生。
      // 注意 failedField 必须**按字段**记,不能用一个全局布尔 ——
      // 否则点「复制标题」失败会把「复制正文」「复制标签」一起染红,
      // 看起来像三个按钮全坏了(实测踩过这个坑)。
      buzz(60);
      setFailedField(field);
      if (copyFieldTimer.current) clearTimeout(copyFieldTimer.current);
      copyFieldTimer.current = setTimeout(() => setFailedField(null), 1600);
      showToast(`复制${label}失败，请长按选中文案手动复制`);
      setLiveMsg(`复制${label}失败，请长按选中文案手动复制`);
      return false;
    }
    setFailedField(null);
    setCopyField(field);
    if (copyFieldTimer.current) clearTimeout(copyFieldTimer.current);
    copyFieldTimer.current = setTimeout(() => setCopyField(null), 1600);
    buzz([10, 30, 10]);
    // T-Cron-2026-10-28 (T118):带字数。
    //
    // 为什么字数要跟着复制反馈走(老仓库 T-Cron-2026-10-07 已验证的做法):
    //   小红书正文上限 1000 字、抖音 220 字 —— 用户复制完**立刻**就要知道
    //   这段有多长,否则只能切到编辑器里才知道,超了还要回来重新删减。
    //   把「856 字」放进 toast,省掉一整轮往返。
    //
    // 字数口径统一用 harness/postText.js 的 countCopyChars
    // (CJK 逐字 + 英文/数字按词),详情复制 / 批量复制 / 历史列表三处同源。
    const chars = countCopyChars(text);
    // T-Cron-2026-10-28 (T118):每次成功复制都进历史。
    recordCopy(text, field, activeVersion);
    showToast(`已复制${label} · ${chars} 字`);
    setLiveMsg(`已复制${label}，共 ${chars} 字`);
    return true;
  };

  React.useEffect(() => () => {
    if (copyFieldTimer.current) clearTimeout(copyFieldTimer.current);
  }, []);

  // T-Cron-2026-10-28 03:30 (T118) — 改进清单 #1「复制体验」:复制历史的交互。
  //
  // 点击历史条目 = **再复制一次那段文本**(而不是展开预览)。
  // 为什么不做「展开预览」:
  //   复制历史存在的唯一理由就是「我刚才复制过的那段,再给我一次」。
  //   做成展开预览等于又多一个「看看」的功能,但用户要的是「拿回」。
  //   ⇒ 点一下就直接进剪贴板,和当初复制它的那一下完全同语义。
  //
  // 失败也要有反馈(历史里的文案可能是很久以前的,内容一样会触发
  // 剪贴板权限问题)⇒ 复用 copyFieldAndFeedback 的三段式失败反馈。
  const [historyCopied, setHistoryCopied] = React.useState('');
  const copyHistoryEntry = async (entry) => {
    if (!entry || !entry.text) return;
    const ok = await copyTextToClipboard(entry.text);
    if (!ok) {
      buzz(60);
      showToast('复制失败，请长按选中文案手动复制');
      setLiveMsg('复制失败，请长按选中文案手动复制');
      return;
    }
    buzz([10, 30, 10]);
    setHistoryCopied(entry.text);
    setTimeout(() => setHistoryCopied(''), 1600);
    showToast(`已重新复制 · ${entry.charCount || countCopyChars(entry.text)} 字`);
    setLiveMsg(`已重新复制上次内容，共 ${entry.charCount || countCopyChars(entry.text)} 字`);
  };

  // 历史条目的展示标签(按 kind)。
  // 用中文短标签而不是 kind 原值,因为用户看到的是「正文」「标题」这种词。
  const copyKindLabel = (kind) => ({
    full: '全文',
    all: '三版',
    title: '标题',
    body: '正文',
    tags: '标签',
  }[kind] || '文案');

  // 历史条目的摘要文本:折叠空白 + 截断,保证 chip 高度稳定。
  const copyPreview = (text) => {
    const flat = String(text || '').replace(/\s+/g, ' ').trim();
    return flat.length > 18 ? `${flat.slice(0, 18)}…` : flat;
  };

  // T-Cron-2026-10-22 14:00 — 改进清单 #4:让全局快捷键 `c` 能触发详情里的复制。
  // 用 CustomEvent 而不是把文案组装逻辑复制到 App.jsx:
  // 文案拼装(title + body + tags)只应该有一份实现,复制到 App 里必然漂移。
  // 事件只在抽屉打开时才有意义,所以 open=false 时不挂监听。
  //
  // 🔴🔴 T135(2026-11-02 20:00)修 P0:按 `c` 复制的是**旧版本**的文案。
  //
  // 【现象】抽屉里显示的是 B 版(「鸿蒙Next背后,不止技术」),
  //   按 `c` 复制到剪贴板的却是 A 版(「鸿蒙Next来了,华为凭什么」)。
  //   而点抽屉里的「复制文案」按钮是对的 —— 只有键盘路径错。
  //
  // 【根因:事件监听器捕获了陈旧闭包】
  //   下面的 effect 依赖数组是 [open]。用户在抽屉里切 A→B 时,
  //   open 一直是 true ⇒ effect **不重跑** ⇒ 监听器仍然握着
  //   「抽屉刚打开那一刻」的 handleCopy 闭包,而那个闭包里的
  //   versionData / tagsLine / activeVersion 全是**打开时的旧值**。
  //   ⇒ 按钮(onClick 每帧新建闭包)是对的,事件监听(注册一次、闭包冻结)错。
  //
  // 【修法:latest-ref(不是把 versionData 塞进依赖数组)】
  //   如果把 versionData 塞进 deps,每次切版本都会 remove/add 监听器 ——
  //   能修好,但属于「用重订阅掩盖闭包过期」,且和 T131/T132 那条铁律
  //   (不要让 effect 去追外部变化)同族。
  //   正确做法:render 期把最新的 handleCopy 写进 ref,监听器只注册一次,
  //   调用时读 ref.current ⇒ **永远是本帧最新的实现**,零陈旧窗口。
  //   (和 harness/useKeyboardShortcuts.js 的 optsRef.current = opts 同一套约定。)
  const copyHandlerRef = React.useRef(null);
  copyHandlerRef.current = handleCopy;
  const copyAllHandlerRef = React.useRef(null);
  copyAllHandlerRef.current = handleCopyAllVersions;

  React.useEffect(() => {
    if (!open) return;
    const onCopyRequest = () => { copyHandlerRef.current(); };
    window.addEventListener('xhs:copy-detail-text', onCopyRequest);
    return () => window.removeEventListener('xhs:copy-detail-text', onCopyRequest);
  }, [open]);

  // T-Cron-2026-10-26 03:30 早间 — 快捷键 Shift+C = 复制 A/B/C 三版。
  // 同样走 CustomEvent(和上面的单键 c 同约定)—— App.jsx 只负责发事件,
  // 不在这里重复实现文案组装。
  //
  // T135:同样改用 latest-ref(文案组装本身与版本无关,但保持同一套约定,
  // 避免以后有人给它加上版本相关逻辑时又踩同一个坑)。
  React.useEffect(() => {
    if (!open) return;
    const onCopyAllRequest = () => { copyAllHandlerRef.current(); };
    window.addEventListener('xhs:copy-detail-all-versions', onCopyAllRequest);
    return () => window.removeEventListener('xhs:copy-detail-all-versions', onCopyAllRequest);
  }, [open]);

  // Close on Escape
  //
  // T-Cron-2026-11-01 08:00 早间:保存面板(下载弹层)叠在详情抽屉**之上**,
  // 两者都监听 Escape ⇒ 一次按下会把面板和抽屉一起关掉,
  // 用户直接被踢回看板列表(详情也白开了)。
  // ⇒ 这里显式判断 sheet:面板开着时,这次 Escape 归面板处理,抽屉不动。
  //   (面板自己的 handler 在 document capture 阶段先跑并 stopPropagation,
  //    但显式判断不依赖事件传播顺序,更稳。)
  React.useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      // 🔴 T137 (2026-11-03 08:00 早间) — 长按(auto-repeat)守卫。
      if (e.repeat && !(e.ctrlKey || e.metaKey || e.altKey)) return;
      if (e.key === 'Escape') {
        if (sheet) return; // 保存面板开着 → 让给面板,抽屉不关
        onClose();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose, sheet]);

  // T-Cron-2026-10-28 03:30 (T118) — 改进清单 #2「快捷键」补齐:
  // **详情抽屉内的键盘操作**。
  //
  // 🔴 修 bug + 补能力(两件事一起做,因为它们是同一个键位冲突):
  //
  // 1) 修 bug:`1` / `2` 原本由**全局** handler 处理成「切网格/列表视图」。
  //    抽屉打开时用户按 `1` 想切 A 版,结果抽屉背后的看板闪了一下,
  //    抽屉里的版本纹丝不动 ⇒ 看起来像「按了没反应」。
  //    已在 useKeyboardShortcuts.js 的 `1`/`2` 分支加 isDetailOpen 守卫
  //    (在源头让位,比 stopPropagation 可靠 —— 两个 listener 都挂在
  //     document 冒泡阶段,同阶段时执行顺序取决于注册先后)。
  //
  // 2) 补能力:抽屉内现在自己处理数字键 → 切版本。
  //    老仓库(根目录 components/PostDetail.jsx:412-447)就是这个约定,
  //    Vite 重写时整段丢了 ⇒ 「卡片上能按数字切版本,进到详情反而不能」。
  //
  // 老仓库同款约定(逐条对齐,不发明新的):
  //   1 / 2 / 3  → 切 A / B / C 版(只响应真实存在的版本)
  //   ← / →      → 上一个 / 下一个版本(循环)
  //   d          → 下载当前版封面
  //   s          → 调起系统分享(封面)
  //
  // `c` / `Shift+C` / `f` 保持由**全局** handler 处理(App.jsx 已接线),
  // 这里不重复实现 —— 两处都实现会让同一次按键触发两次。
  //
  // 挂在 **capture 阶段**的 window 上:详情打开时,抽屉里的按键应该
  // 优先于看板的全局快捷键(和老仓库一致),而且 capture 阶段的执行顺序
  // 是确定的(先于冒泡),不依赖注册顺序。
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      // 输入态让位(抽屉里可能有搜索框等)。
      const tag = (e.target && e.target.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.target && e.target.isContentEditable) return;

      // 组合键放行(浏览器/系统快捷键)。
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      // 🔴 T137 (2026-11-03 08:00 早间) — 长按(auto-repeat)守卫。
      //
      // 【为什么这一层必须自己挡】全局 handler(useKeyboardShortcuts.js)
      //   也有同样的守卫,但它是 **document 冒泡**阶段,而这里是
      //   **window 捕获**阶段 —— 捕获阶段**先跑** ⇒ 全局守卫根本轮不到
      //   这里 ⇒ 抽屉里的 d/s/数字/方向键必须**自己**挡一次。
      //
      // 【具体症状】
      //   按住 d 不放 1 秒 ⇒ auto-repeat 每 ~30ms 触发一次
      //     handleSaveCover ⇒ 下载栏刷出 5~10 份封面(每份 200-600KB)
      //   按住 → 不放 ⇒ 版本 A→B→A 来回翻,每次还 buzz(6) 震动
      //     ⇒ 用户想要「下一个版本」,结果停在错误版本 + 马达连响
      //
      // 【组合键放行在前】Alt+↓ 之类的组合键 repeat 是期望行为
      //   ⇒ repeat 判断放在组合键判断之后。
      if (e.repeat) return;

      const keys = post && post.versions ? Object.keys(post.versions).sort() : [];

      // 数字键 → 切版本。只响应**真实存在**的版本,
      // 避免在只有 A/B 两版的帖子上按 `3` 时切到一个不存在的版本。
      if (/^[1-9]$/.test(e.key) && keys.length) {
        const idx = Number(e.key) - 1;
        if (idx < 0 || idx >= keys.length) return;
        e.preventDefault();
        e.stopPropagation();
        if (keys[idx] !== activeVersion) {
          buzz(6);
          setUserVersion(keys[idx]);
        }
        return;
      }

      // ← / → → 上一个 / 下一个版本(循环)。
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        if (!keys.length) return;
        e.preventDefault();
        e.stopPropagation();
        const idx = keys.indexOf(activeVersion);
        const next = e.key === 'ArrowLeft'
          ? keys[(idx - 1 + keys.length) % keys.length]
          : keys[(idx + 1) % keys.length];
        if (next !== activeVersion) {
          buzz(6);
          setUserVersion(next);
        }
        return;
      }

      // d → 下载当前版封面。
      if (e.key === 'd' || e.key === 'D') {
        if (e.shiftKey) return;
        e.preventDefault();
        e.stopPropagation();
        handleSaveCover(activeVersion);
        return;
      }

      // s → 调起系统分享(封面)。
      // Shift+S 保留(和全局的 Shift+F / Shift+R 一样,大写键留给未来的
      // 「分享全部版本」,不劫持)。
      if (e.key === 's' || e.key === 'S') {
        if (e.shiftKey) return;
        e.preventDefault();
        e.stopPropagation();
        handleShareCover();
        return;
      }
    };
    window.addEventListener('keydown', onKey, true); // capture:先于看板的全局 handler
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, post, activeVersion]);

  // T-Cron-2026-10-22 08:00 早间 (round-1-of-day) — a11y #10:
  // 焦点陷阱。之前这个抽屉是 role="dialog" 但**没有** aria-modal,
  // 键盘用户打开详情后可以 Tab 到背后的看板卡片上,焦点跑丢。
  // 现在:aria-modal 声明模态 + useFocusTrap 把焦点锁在抽屉里,
  // 关闭时把焦点还给触发卡片。
  const drawerRef = React.useRef(null);
  // 🔴 T-Cron-2026-11-01 08:00:保存面板是抽屉的**兄弟节点**(不是子节点),
  //   所以面板开着时抽屉的陷阱仍然是「启用」状态 ⇒ 它的 Tab 处理器看到
  //   `!panel.contains(active)`(焦点在面板里)就会把焦点**拽回抽屉**,
  //   和面板自己的陷阱打架 ⇒ Tab 行为不可预测。
  //   ⇒ 面板开着时停用抽屉的陷阱,让面板独占焦点管理。
  //   (面板关掉后 enabled 变回 true,抽屉的陷阱重新接管。)
  //
  // 🔴 T-Cron-2026-11-03 08:00 早间 (T139) — 补 `restoreFocus: !sheet`。
  //
  // 【为什么必须显式声明,不能靠 hook 自己猜】
  //   面板开着 ⇒ 抽屉的 trap 被禁用 ⇒ effect cleanup 跑 ⇒ 若 cleanup 把焦点
  //   还给抽屉背后的卡片,焦点就跑到**模态背后**了(用户看不见自己在哪)。
  //   面板关掉 ⇒ 抽屉的 trap 重新启用 ⇒ effect 重跑 ⇒ autoFocus 又把焦点
  //   拉到抽屉里的**第一个**可聚焦元素(「关闭」)⇒ 用户被拽回抽屉顶部,
  //   丢失了刚才在封面按钮上的位置。
  //   实测(修复前):打开保存面板 → 关闭 ⇒ 焦点落在「关闭」而不是
  //   「保存 A 版封面」⇒ 位置丢失。
  //
  //   ⇒ `restoreFocus: !sheet` 的语义:
  //     · sheet 开着(抽屉只是临时让位)⇒ 不还焦点 ⇒ 焦点留在面板里 ✓
  //     · sheet 关了(抽屉真的要关了)⇒ 还焦点 ⇒ 焦点回到打开抽屉的卡片 ✓
  //   ⇒ 另一处修法在 hook 里:autoFocus 只在「焦点不在面板内」时才抢
  //     (否则重新启用时会把刚归还的焦点又拽走)。
  useFocusTrap(drawerRef, { enabled: open && !sheet, autoFocus: true, restoreFocus: !sheet });

  // T-Cron-2026-11-01 08:00 早间 — a11y #10 补齐「保存封面面板」的模态语义。
  //
  // 🔴 为什么这是真缺口(不是"锦上添花"):
  //   下载面板是**全站使用频率最高的二级弹层**(长按封面 → 弹出保存菜单),
  //   而它是本项目**唯一**一个 role="dialog" 但没有 aria-modal、
  //   没有焦点陷阱、没有 Escape 关闭的弹层。
  //   对比:详情抽屉和移动端筛选抽屉都接了 useFocusTrap + Escape。
  //   ⇒ 键盘/开关控制用户打开保存面板后:
  //     · Tab 会跑到**背后的看板卡片**上(焦点跑丢,不知道自己在哪);
  //     · Escape 关不掉(只能去够「关闭」按钮或点遮罩);
  //     · 读屏不知道这是模态,可能继续往后面的内容读。
  //   ⇒ 这是"已上线但部分用户用不了"的可用性缺陷,
  //     优先级高于任何新功能(与 T128 同一个判断标准)。
  //
  // 为什么用 useFocusTrap 而不是自己写一套:
  //   harness/useFocusTrap.js 已经实现了"记住打开前焦点 → 关闭时还回去",
  //   与详情抽屉/筛选抽屉同源 ⇒ 行为一致,不多一份实现。
  //   enabled 用 `!!sheet` —— 面板是**条件挂载**的(sheet 为 null 时整个
  //   JSX 不渲染),所以 effect 里的 panelRef.current 在挂载瞬间才有值。
  //
  // 🔴 React 坑(与 T128 的 FAB 教训同源):
  //   useFocusTrap 内部用 useEffect + panelRef,**依赖数组是 [enabled,
  //   autoFocus, panelRef]** —— panelRef 是 ref 对象,身份恒定。
  //   ⇒ 只要 enabled 从 false 变 true,effect 就会重跑,
  //     此时 sheet 已经在 state 里 ⇒ 面板已渲染 ⇒ panelRef.current 有值。
  //   ⇒ 这里必须用 `!!sheet` 作为 enabled(而不是另开一个 openSheet state),
  //     否则会出现"面板已渲染但陷阱没装上"的时序错位。
  const sheetRef = React.useRef(null);
  useFocusTrap(sheetRef, { enabled: !!sheet, autoFocus: true });

  // Escape 关闭保存面板。
  //
  // 为什么不用 useFocusTrap 自带的能力:它只管 Tab 循环,不管 Escape。
  // 详情抽屉是在自己的 keydown handler 里处理 Escape 的(见上文 onKey),
  // 保存面板这里保持同一套口径。
  //
  // 🔴 关键:必须同时挡住**详情抽屉**的 Escape。
  //   保存面板是在详情抽屉**之上**叠的一层(用户先开详情,再长按封面),
  //   两个 Escape handler 都挂在 document 上 ⇒ 不加 stopPropagation 的话,
  //   按一次 Escape 会**同时**关掉面板和详情抽屉(用户直接被踢回列表)。
  //   ⇒ 这里用 capture 阶段 + stopPropagation,让面板"吃掉"这次 Escape。
  //   触发顺序:window capture → document capture → ... → document bubble
  //   ⇒ window capture 是**最早**的,详情抽屉的 handler 在 window capture
  //     (line 642 是 window.addEventListener(..., true))—— 两者都在 capture。
  //     window 上的先执行(window → document → 目标是 document 内部),
  //     所以详情抽屉的 handler **会先跑** ⇒ 光靠 stopPropagation 不够。
  //   ⇒ 正确做法:在详情抽屉的 Escape handler 里判断"保存面板是否开着",
  //     开着就跳过(由面板自己处理)。下面两处都要改。
  React.useEffect(() => {
    if (!sheet) return undefined;
    const onSheetKey = (e) => {
      // 🔴 T137 (2026-11-03 08:00 早间) — 长按(auto-repeat)守卫。
      if (e.repeat && !(e.ctrlKey || e.metaKey || e.altKey)) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setSheet(null);
      }
    };
    document.addEventListener('keydown', onSheetKey, true);
    return () => document.removeEventListener('keydown', onSheetKey, true);
  }, [sheet]);

  if (!post || !versionData) return null;

  return (
    <>
      <div
        className={`drawer-overlay ${open ? 'open' : ''}`}
        onClick={onClose}
      />
      <aside
        ref={drawerRef}
        className={`drawer ${open ? 'open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="内容详情"
        /* tabIndex={-1}:当抽屉里一个可聚焦元素都没有时,焦点陷阱需要一个
           落点把焦点钉住(否则焦点会飘到 body 上)。 */
        tabIndex={-1}
        style={dragOffset > 0 ? { transform: `translateY(${dragOffset}px)` } : undefined}
      >
        <div
          className="drawer-handle"
          onTouchStart={handleDragStart}
          onTouchMove={handleDragMove}
          onTouchEnd={handleDragEnd}
          aria-hidden="true"
        />
        <div className="drawer-header">
          <button className="drawer-close" onClick={onClose} aria-label="关闭">
            <CloseIcon />
          </button>
          <div className="drawer-actions">
            <button
              className={`btn-outline ${showCopyPulse ? 'copy-success' : ''}`}
              onClick={handleCopy}
            >
              <CopyIcon />
              {copied ? '已复制 ✓' : '复制文案'}
            </button>
            <button
              className="btn-primary"
              onClick={() => { buzz(15); onToggleFav(post.id); }}
            >
              {post.favorite ? '♥ 已选材' : '♥ 标记选材'}
            </button>
          </div>
        </div>

        <div
          className="drawer-body"
          onTouchStart={handleDragStart}
          onTouchMove={handleDragMove}
          onTouchEnd={handleDragEnd}
        >
          {detailCoverUrl && !detailCoverBroken ? (
            <div style={{ position: 'relative' }}>
              <img
                key={`${post.id}-${activeVersion}`}
                className="detail-cover"
                src={detailCoverUrl}
                alt={post.book}
                // T-Cron-2026-11-01 08:00 — 改进清单 #13「性能:图片懒加载」。
                // 这一张是详情抽屉的**主视觉**,打开抽屉就要立刻看到 ⇒ 显式
                // loading="eager"(不给 lazy,否则首帧会空一下再冒出来,
                // 反而更慢更难看)。decoding="async" 让浏览器在后台解码,
                // 不阻塞首屏渲染。
                loading="eager"
                decoding="async"
                // 🔴 T114:封面取不到时降级(而不是留下浏览器的碎图标)。
                onError={() => setDetailCoverBroken(true)}
                onContextMenu={(e) => { e.preventDefault(); buzz(15); }}
                onTouchStart={(e) => {
                  // 移动端长按直接调起保存面板
                  const t = e.currentTarget;
                  t._lpTimer = setTimeout(() => {
                    buzz(25);
                    setSheet({ ver: activeVersion, url: detailCoverUrl });
                    // 给视觉反馈
                    const hint = t.parentElement.querySelector('.cover-longpress-hint');
                    if (hint) {
                      hint.classList.add('visible');
                      setTimeout(() => hint.classList.remove('visible'), 700);
                    }
                  }, 500);
                }}
                onTouchEnd={(e) => {
                  if (e.currentTarget._lpTimer) {
                    clearTimeout(e.currentTarget._lpTimer);
                    e.currentTarget._lpTimer = null;
                  }
                }}
                onTouchMove={(e) => {
                  if (e.currentTarget._lpTimer) {
                    clearTimeout(e.currentTarget._lpTimer);
                    e.currentTarget._lpTimer = null;
                  }
                }}
              />
              <div className="cover-longpress-hint">松手保存封面</div>
            </div>
          ) : (
            <div className={`detail-cover${detailCoverBroken ? ' is-broken' : ''}`}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '8px', color: 'var(--text-tertiary)', fontFamily: 'var(--font-serif)', fontSize: 20 }}>
              {detailCoverBroken ? (
                <>
                  <span className="pc-cover-broken-icon" aria-hidden="true">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                      <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                      <circle cx="8.5" cy="8.5" r="1.5"></circle>
                      <path d="M21 15l-5-5L5 21"></path>
                    </svg>
                  </span>
                  <span className="pc-cover-broken-text">封面暂不可用</span>
                  <span className="pc-cover-broken-book">《{post.book}》</span>
                </>
              ) : (
                <>《{post.book}》</>
              )}
            </div>
          )}

          {/* T-Cron-2026-10-23 08:00 — 改进清单 #5「下载封面进度条」。
              封面 200–600KB,弱网下要几秒。之前只有一句不动的「处理中…」,
              用户无法判断是在下载还是卡死 ⇒ 容易重复点击(并发重复下载)。
              现在:已知大小时显示百分比 + 进度条;未知大小(无 content-length
              或老 webview)时显示不确定态条纹进度条。

              **位置说明(视觉走查后修正)**:第一版把这个进度条放在下载按钮
              *下方*,但详情抽屉首屏只到「保存A/B/C封面」按钮,进度条落在
              折叠线以下 ⇒ 用户在弱网时恰恰看不到它,等于没做。
              现在**贴在封面图正下方**,点任意版本的保存按钮时进度条就出现在
              视线里,不用滚动。 */}
          {dlProgress && (
            <div className="dl-progress" role="progressbar" aria-label="封面下载进度"
              aria-valuenow={dlProgress.total ? Math.round((dlProgress.loaded / dlProgress.total) * 100) : undefined}
              aria-valuemin={0} aria-valuemax={100}>
              <div className="dl-progress-head">
                <span className="dl-progress-label">
                  {dlProgress.total
                    ? `正在下载封面 ${Math.round((dlProgress.loaded / dlProgress.total) * 100)}%`
                    : '正在下载封面…'}
                </span>
                {dlProgress.total > 0 && (
                  <span className="dl-progress-bytes">
                    {(dlProgress.loaded / 1024).toFixed(0)} / {(dlProgress.total / 1024).toFixed(0)} KB
                  </span>
                )}
              </div>
              <div className={`dl-progress-track ${dlProgress.total ? '' : 'indeterminate'}`}>
                <div
                  className="dl-progress-fill"
                  style={dlProgress.total
                    ? { width: `${Math.min(100, (dlProgress.loaded / dlProgress.total) * 100)}%` }
                    : undefined}
                />
              </div>
            </div>
          )}

          {/* 每个版本独立下载：A/B/C 各自可下载封面 */}
          {/* 🔴 T114:封面取不到时不再显示保存按钮 ——
              保存一张取不到的封面只会得到一个打不开的 HTML 文件
              (CF Pages 对不存在的静态文件返回 200 + index.html)。 */}
          {detailCoverUrl && !detailCoverBroken && (
            <div className="detail-cover-actions">
              {(hasAB ? versionKeys : [activeVersion]).map(ver => {
                const vData = getVersionData(post, ver);
                const hasCover = (vData && vData.coverUrl) || post.coverUrl;
                if (!hasCover) return null;
                return (
                  <button
                    key={ver}
                    className={`detail-cover-dl-btn ${activeVersion === ver ? 'current' : ''}`}
                    onClick={handleSaveCover.bind(null, ver)}
                    disabled={downloading === ver}
                    title={`保存${ver}版封面（长按保存 / 分享 / 下载多方案）`}
                    aria-label={`保存 ${ver} 版封面${downloading === ver ? '，正在下载' : ''}`}
                  >
                    <DownloadIcon />
                    <span>{downloading === ver ? '处理中…' : hasAB ? `保存${ver}版封面` : '保存封面'}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* T-Cron-2026-10-23 08:00 — 改进清单 #5「下载封面进度条」已上移到封面图正下方。 */}

          {/* A/B/C Version Toggle - Detail view, driven by available versions */}
          {hasAB && (
            <div className="detail-version-section">
              <div className="detail-version-toggle">
                {versionKeys.map(ver => (
                  <button
                    key={ver}
                    className={`detail-version-btn ${activeVersion === ver ? 'active' : ''}`}
                    onClick={() => setUserVersion(ver)}
                    /* T-Cron-2026-10-28 20:00 晚间 (T121) — 改进清单 #10「无障碍」。
                       🔴 这里是**真实缺口**,而且是**两个组件不一致**造成的:
                         · PostCard 的版本切换器早就有 aria-label + aria-pressed
                           (T-Cron-2026-10-22 补的);
                         · 但详情抽屉里的这一份**漏了** ⇒ 同一个概念(选版本),
                           卡片上读屏念得出「已按下」,抽屉里念不出。
                       抽屉恰恰是用户**做选择**的地方(要看 A/B/C 哪个角度),
                       读屏用户在这里完全不知道当前是第几版。
                       ⇒ 补 aria-label(干净语义)+ aria-pressed(选中状态)。 */
                    aria-label={`切换到 ${ver} 版 ${(post.versions[ver] && post.versions[ver].versionLabel) || ver}`}
                    aria-pressed={activeVersion === ver}
                  >
                    <span className="detail-version-letter">{ver}</span>
                    <span className="detail-version-name">{(post.versions[ver] && post.versions[ver].versionLabel) || ver}</span>
                  </button>
                ))}
              </div>
              <div className="detail-version-desc">
                {versionData.versionDesc}
              </div>
              {/* T-Cron-2026-10-26 03:30 早间 — 改进清单 #1「复制体验」:
                  **一键复制 A/B/C 三版**。
                  老仓库 PostDetail 有 handleCopyAll(Shift+C),Vite 重写时丢了 ——
                  这是一个「清单说完成 ≠ 线上可用」的可达性缺口(同 T104 的 #7、
                  T107 的 #14 形态)。详见 handleCopyAllVersions 上方注释。
                  按钮放在版本切换器正下方 —— 用户看到 A/B/C 三个 tab 时,
                  「把三个都复制走」的想法正好在这里冒出来。 */}
              <button
                className="detail-copy-all-btn"
                onClick={handleCopyAllVersions}
                aria-label={`复制《${post.book}》的 A/B/C 全部版本文案`}
                aria-keyshortcuts="Shift+C"
                title="把 A/B/C 三版拼成一段文本复制走(Shift+C)"
              >
                <CopyIcon />
                <span>复制三版</span>
              </button>
            </div>
          )}

          {/* T-Cron-2026-10-28 03:30 (T118) — 改进清单 #2「快捷键」补齐:
              **快捷键提示条**。

              为什么必须有:老仓库根目录 styles.css:4772 有
              `.detail-shortcut-hints`(kbd 样式的一行提示),Vite 重写时丢了。
              没有提示条的快捷键 = 不存在的快捷键 —— 用户不知道按什么,
              就永远不会用,功能等于白做(同 T105 记录的
              「清单说完成 ≠ 线上可用」形态)。

              放在版本切换器**正下方**:那个位置正好是用户盯着 A/B/C 三个
              tab 的时候,「按数字切版本」的想法刚冒出来。

              只在**桌面端**显示:移动端没有物理键盘,提示条是噪音
              (和老仓库一致:max-width 768px 隐藏)。

              内容跟着实际能力走 —— 只列**真的能用**的键:
                · 1/2/3 → 切版本(有几个版本就列几个)
                · ← →   → 上/下一版
                · C     → 复制当前版(全局 handler)
                · Shift+C → 复制三版(全局 handler)
                · D     → 下载封面
                · S     → 分享封面
              每一项都带 aria-hidden 的 kbd 标签,读屏用户读到的是
              「切到 A 版」这样的文字,而不是「1」。 */}
          <div className="detail-shortcut-hints" aria-hidden="true">
            <span className="detail-shortcut-hint-label">快捷键</span>
            {versionKeys.slice(0, 3).map((ver, i) => (
              <span key={ver} className="detail-shortcut-hint">
                <kbd>{i + 1}</kbd> {ver}版
              </span>
            ))}
            {versionKeys.length > 1 && (
              <>
                <span className="detail-shortcut-hint"><kbd>←</kbd><kbd>→</kbd> 切版</span>
                <span className="detail-shortcut-hint"><kbd>Shift</kbd>+<kbd>C</kbd> 复制三版</span>
              </>
            )}
            <span className="detail-shortcut-hint"><kbd>C</kbd> 复制本版</span>
            <span className="detail-shortcut-hint"><kbd>D</kbd> 下载封面</span>
            <span className="detail-shortcut-hint"><kbd>S</kbd> 分享封面</span>
          </div>

          <div className="detail-book-tag">📖 {post.book}{post.author ? ` · ${post.author}` : ''}</div>
          <div className="detail-title-row">
            <h2 className="detail-title">{versionData.title}</h2>
            <button
              className={`detail-copy-chip ${copyField === 'title' ? 'is-copied' : ''} ${failedField === 'title' ? 'is-failed' : ''}`}
              onClick={() => copyFieldAndFeedback('title', versionData.title, '标题')}
              aria-label={`复制标题：${versionData.title}`}
            >
              {copyField === 'title' ? '已复制 ✓' : '复制标题'}
            </button>
          </div>
          <div className="detail-title-count">
            标题共 <strong className={versionData.title.length > 19 ? 'over' : ''}>{versionData.title.length}</strong> 字
            {versionData.title.length > 19 ? (
              <span className="detail-title-warn">超出 {versionData.title.length - 19} 字（≤19字为宜）</span>
            ) : (
              <span className="detail-title-ok">符合 19 字以内要求</span>
            )}
          </div>

          <div className="detail-meta-row">
            <div className="detail-meta-item">
              <strong>发布日期：</strong>
              <span>{post.publishDate || ContentService.formatDate(post.date)}</span>
            </div>
            {(post.recommendTime || post.contentCategory) && (
              <div className="detail-meta-item">
                <strong>推荐发布时间：</strong>
                <span>
                  {post.recommendTime || '—'}
                  {post.contentCategory && (
                    <span className="detail-schedule-cat">（{post.contentCategory}时段）</span>
                  )}
                </span>
              </div>
            )}
            {post.contentCategory && (
              <div className="detail-meta-item">
                <strong>内容分类：</strong>
                <span>{post.contentCategory}</span>
              </div>
            )}
            {post.category && (
              <div className="detail-meta-item">
                <strong>分类：</strong>
                <span>{post.category}</span>
              </div>
            )}
            {post.batch && (
              <div className="detail-meta-item">
                <strong>批次：</strong>
                <span>{post.batch}深度版</span>
              </div>
            )}
            {post.needsReview && (
              <div className="detail-meta-item">
                <strong>状态：</strong>
                <span className="detail-review-flag">待人工复核（周一批次）</span>
              </div>
            )}
            <div className="detail-meta-item">
              <strong>角度：</strong>
              <span>{versionData.angle}</span>
            </div>
            <div className="detail-meta-item">
              <strong>字数：</strong>
              <span>{totalWordCount}字（含标签）</span>
            </div>
            <div className="detail-meta-item">
              <strong>序号：</strong>
              <span>#{post.seq}</span>
            </div>
            {/* T-Cron-2026-10-24 14:00 (round-2-of-day) — 改进清单 #9「view count」。
                单篇的浏览次数。数据来自 harness/viewCounts.js(localStorage),
                只在打开详情时 +1。计数是**使用者本地**的行为记录,
                不是公开的"热度"指标 —— 措辞用"已读"而不是"浏览量",
                避免被误读成全站排名。 */}
            {viewCount > 0 && (
              <div className="detail-meta-item">
                <strong>已读：</strong>
                <span>{viewCount} 次</span>
              </div>
            )}
          </div>

          {post.hotTopic && (
            <div className="detail-hot-topic">
              <strong>🔥 热点关联</strong>
              <div className="detail-hot-topic-name">{post.hotTopic}</div>
              {(post.hotTopicFull || post.hotTopicDetail) && (
                <div className="detail-hot-topic-detail">{post.hotTopicFull || post.hotTopicDetail}</div>
              )}
            </div>
          )}

          <div className="detail-body-row">
            <div className="detail-body">
              {versionData.body}
              {tagsLine && (
                <span className="detail-body-tags">{'\n\n'}{tagsLine}</span>
              )}
            </div>
            <div className="detail-copy-row">
              <button
                className={`detail-copy-chip ${copyField === 'body' ? 'is-copied' : ''} ${failedField === 'body' ? 'is-failed' : ''}`}
                onClick={() => copyFieldAndFeedback('body', versionData.body, '正文')}
                aria-label={`复制${activeVersion} 版正文`}
              >
                {copyField === 'body' ? '已复制 ✓' : '复制正文'}
              </button>
              {tagsList.length > 0 && (
                <button
                  className={`detail-copy-chip is-secondary ${copyField === 'tags' ? 'is-copied' : ''} ${failedField === 'tags' ? 'is-failed' : ''}`}
                  onClick={() => copyFieldAndFeedback('tags', tagsLine, '标签')}
                  aria-label={`复制标签：${tagsLine}`}
                >
                  {copyField === 'tags' ? '已复制 ✓' : '复制标签'}
                </button>
              )}
            </div>
          </div>

          {/* T-Cron-2026-10-28 03:30 (T118) — 改进清单 #1「复制体验」:最近复制历史。
              位置说明:放在正文块**正下方**,紧跟着刚点过的复制按钮 ——
              用户的注意力本来就在那儿,历史条出现在视线里才有用。
              放在抽屉底部(老仓库的位置)会落到折叠线以下,等于没做。

              为什么这个控件值得做(不是「有历史」本身,而是「再拿一次」):
                用户复制完 A 版正文去小红书粘贴,发现开头不够抓人,想改用 B 版,
                或者想把标题也带上重新拼一版 —— 这时他要的是「刚才那段」
                而不是重新走一遍「关抽屉→找卡片→开→切版本→复制」。
                老仓库(根目录 components/PostDetail.jsx)T43 就有这个控件,
                Vite 重写时丢了 ⇒ 本轮按老仓库语义移植回来。

              点一下 = 再复制一次(见 copyHistoryEntry 注释)。 */}
          {copyHistory.length > 0 && (
            <div className="detail-copy-history" aria-label="最近复制">
              <div className="detail-copy-history-head">
                <span className="detail-copy-history-title">最近复制</span>
                <button
                  className="detail-copy-history-clear"
                  onClick={() => { clearCopies(); showToast('已清空复制历史'); }}
                  aria-label="清空最近复制历史"
                  title="清空最近复制历史"
                >
                  清空
                </button>
              </div>
              <div className="detail-copy-history-list">
                {copyHistory.map((entry, idx) => (
                  <button
                    key={`${entry.ts}-${idx}`}
                    className={`detail-copy-history-chip ${historyCopied === entry.text ? 'is-copied' : ''}`}
                    onClick={() => copyHistoryEntry(entry)}
                    title={entry.text}
                    aria-label={`重新复制:${copyKindLabel(entry.kind)} ${entry.charCount || countCopyChars(entry.text)} 字,${copyPreview(entry.text)}`}
                  >
                    <span className="detail-copy-history-kind">{copyKindLabel(entry.kind)}</span>
                    <span className="detail-copy-history-text">{copyPreview(entry.text)}</span>
                    {entry.charCount > 0 && (
                      <span className="detail-copy-history-count">{entry.charCount}字</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* 下载结果提示。
          T-Cron-2026-10-31:换成共享 DismissibleToast —— 支持点击 / Esc 关闭,
          且 tabIndex=0 让键盘用户能主动关掉挡住内容的提示条。 */}
      <DismissibleToast
        message={toastMsg}
        onDismiss={() => {
          setToastMsg('');
          if (toastTimer.current) clearTimeout(toastTimer.current);
        }}
      />
      {/* 读屏专用播报区:复制成功/失败即时播报。
          视觉用户看到的是按钮文案切换 + toast,这里 aria-live 区域只被
          读屏软件读取,不会造成视觉重复。 */}
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {liveMsg}
      </div>


      {/* 多方案保存面板：长按保存为主，分享/下载/复制链接/浏览器打开兜底 */}
      {sheet && (
        <div className="download-sheet-overlay" onClick={() => setSheet(null)}>
          {/*
            T-Cron-2026-11-01 08:00 早间 — a11y #10:补齐模态语义。
            ref={sheetRef}  → useFocusTrap 的落点(焦点循环 + 关闭后归还焦点)
            aria-modal="true" → 告诉读屏「背景不可交互」
            tabIndex={-1}      → 面板本身可接住焦点(面板内无可聚焦元素时的兜底)
          */}
          <div
            ref={sheetRef}
            className="download-sheet"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="保存封面"
            tabIndex={-1}
          >
            <div className="download-sheet-title">保存封面（{sheet.ver}版）</div>
            {/* T-Cron-2026-11-01 08:00 — 改进清单 #13「性能:图片懒加载」。
                面板里的封面是**预览图**,真正的图马上就会被长按保存,
                所以它不该跟首屏抢带宽 ⇒ loading="lazy" + decoding="async"。
                (对比:PostCard 的封面是列表图,已经在用 lazy。) */}
            <img
              className="download-sheet-img"
              key={sheet.url}
              src={sheet.url}
              alt={`${post.book} ${sheet.ver}版封面`}
              loading="lazy"
              decoding="async"
            />
            <div className="download-sheet-hint">
              {huaweiEnv ? (
                <>华为/鸿蒙浏览器不支持直接下载。最稳方式：<b>长按上方图片</b> → 选「保存图片」/「存储图像」，即可存入手机相册。</>
              ) : (
                <>最稳方式：<b>长按上方图片</b> → 选「保存图片」/「存储图像」；或使用下方其他方式。</>
              )}
            </div>
            {/* T-Cron-2026-10-23 08:00 — 改进清单 #5:面板内的下载/分享也要有进度。
                长按保存这条路不需要进度(浏览器原生长按菜单自带反馈),
                但「系统分享保存」/「直接下载」这两条要 fetch 整个文件 ⇒ 需要进度条。 */}
            {dlProgress && (
              <div className="dl-progress sheet" role="progressbar" aria-label="封面下载进度"
                aria-valuenow={dlProgress.total ? Math.round((dlProgress.loaded / dlProgress.total) * 100) : undefined}
                aria-valuemin={0} aria-valuemax={100}>
                <div className="dl-progress-head">
                  <span className="dl-progress-label">
                    {dlProgress.total
                      ? `正在准备封面 ${Math.round((dlProgress.loaded / dlProgress.total) * 100)}%`
                      : '正在准备封面…'}
                  </span>
                  {dlProgress.total > 0 && (
                    <span className="dl-progress-bytes">
                      {(dlProgress.loaded / 1024).toFixed(0)} / {(dlProgress.total / 1024).toFixed(0)} KB
                    </span>
                  )}
                </div>
                <div className={`dl-progress-track ${dlProgress.total ? '' : 'indeterminate'}`}>
                  <div
                    className="dl-progress-fill"
                    style={dlProgress.total
                      ? { width: `${Math.min(100, (dlProgress.loaded / dlProgress.total) * 100)}%` }
                      : undefined}
                  />
                </div>
              </div>
            )}
            <div className="download-sheet-actions">
              {canShareCover && (
                <button className="download-sheet-btn primary" onClick={handleSheetShare}>
                  系统分享保存
                </button>
              )}
              {!huaweiEnv && (
                <button className="download-sheet-btn" onClick={handleSheetDownload} disabled={downloading === sheet.ver}>
                  {downloading === sheet.ver ? '下载中…' : '直接下载'}
                </button>
              )}
              <button className="download-sheet-btn" onClick={handleSheetCopyLink}>
                复制图片链接
              </button>
              <a className="download-sheet-btn" href={sheetOpenUrl} target="_blank" rel="noopener noreferrer">
                浏览器打开原图
              </a>
              <button className="download-sheet-btn" onClick={() => setSheet(null)}>
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

window.PostDetail = PostDetail;

export default PostDetail;
